import {
  Box,
  VStack,
  HStack,
  Text,
  IconButton,
  Button,
} from "@chakra-ui/react";
import {
  FiArrowLeft,
  FiList,
  FiMessageCircle,
  FiSettings,
  FiLock,
} from "react-icons/fi";
import IconWrapper from "./IconWrapper";
import VideoControls from "./VideoControls";
import VideoProgressBar from "./VideoProgressBar";
import VideoSettings from "./VideoSettings";
import VideoInteractions from "./VideoInteractions";
import CachedImage from "./CachedImage";

interface VideoPlayerLayoutProps {
  // Video state
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  isDragging: boolean;
  hoverTime: number | null;
  showPreview: boolean;
  bufferedRanges: TimeRanges | null;
  showControls: boolean;
  isLocked: boolean;
  showSkipFeedback: boolean;
  skipAmount: number;

  // Interaction state
  isLiked: boolean;
  isDisliked: boolean;
  isSubscribed: boolean;
  likes: number;
  dislikes: number;
  viewCount: number;
  creatorAvatar?: string;
  uploaderName: string;
  subscriberCount: number;
  creatorId?: string;
  userUid?: string;

  // UI state
  showEpisodes: boolean;
  showSubtitles: boolean;
  playbackSpeed: number;
  isMuted: boolean;
  isFullscreen: boolean;

  // Functions
  formatTime: (seconds: number) => string;
  getProgressPercentage: () => number;
  getBufferedPercent: () => number;
  handlePlayPause: () => void;
  handleSkip: (seconds: number) => void;
  handleProgressMouseMove: (e: React.MouseEvent<HTMLDivElement>) => void;
  handleProgressMouseLeave: () => void;
  handleProgressClick: (e: React.MouseEvent<HTMLDivElement>) => void;
  handleLike: () => void;
  handleDislike: () => void;
  handleSubscribe: () => void;
  handleShare: () => void;
  handleDonate: () => void;
  handleMuteToggle: () => void;
  handleFullscreenToggle: () => void;
  handleDownload: () => void;
  handleRefresh: () => void;
  handleEpisodesToggle: () => void;
  handleSubtitlesToggle: () => void;
  handleSpeedChange: () => void;
  handleLockToggle: () => void;
  onNavigateBack: () => void;
  title: string;
}

const VideoPlayerLayout: React.FC<VideoPlayerLayoutProps> = ({
  isPlaying,
  currentTime,
  duration,
  isDragging,
  hoverTime,
  showPreview,
  bufferedRanges,
  showControls,
  isLocked,
  showSkipFeedback,
  skipAmount,
      isLiked,
      isDisliked,
      isSubscribed,
      likes,
      dislikes,
      viewCount,
      creatorAvatar,
      uploaderName,
      subscriberCount,
  creatorId,
  userUid,
  showEpisodes,
  showSubtitles,
  playbackSpeed,
  isMuted,
  isFullscreen,
  formatTime,
  getProgressPercentage,
  getBufferedPercent,
  handlePlayPause,
  handleSkip,
  handleProgressMouseMove,
  handleProgressMouseLeave,
  handleProgressClick,
  handleLike,
  handleDislike,
  handleSubscribe,
  handleShare,
  handleDonate,
  handleMuteToggle,
  handleFullscreenToggle,
  handleDownload,
  handleRefresh,
  handleEpisodesToggle,
  handleSubtitlesToggle,
  handleSpeedChange,
  handleLockToggle,
  onNavigateBack,
  title,
}) => {
  return (
    <>
      {/* Top Controls */}
      {showControls && (
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          zIndex={10}
          bg="linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, transparent 100%)"
          p={4}
        >
          <HStack justify="space-between" align="center">
            <IconButton
              aria-label="Go back"
              icon={<IconWrapper icon={FiArrowLeft} size={20} />}
              variant="ghost"
              colorScheme="white"
              onClick={onNavigateBack}
            />
            <HStack gap={2} align="center">
              <Text color="white" fontSize="lg" fontWeight="bold" noOfLines={1}>
                {title}
              </Text>
              {isLocked && (
                <Box
                  bg="red.500"
                  color="white"
                  px={2}
                  py={1}
                  borderRadius="md"
                  fontSize="xs"
                  fontWeight="bold"
                >
                  LOCKED
                </Box>
              )}
            </HStack>
            <VideoSettings
              isMuted={isMuted}
              isFullscreen={isFullscreen}
              onMuteToggle={handleMuteToggle}
              onFullscreenToggle={handleFullscreenToggle}
              onDownload={handleDownload}
              onRefresh={handleRefresh}
              onShare={handleShare}
            />
          </HStack>
        </Box>
      )}

      {/* Skip Feedback Overlay */}
      {showSkipFeedback && (
        <Box
          position="absolute"
          top="50%"
          left="50%"
          transform="translate(-50%, -50%)"
          zIndex={15}
          pointerEvents="none"
        >
          <VStack gap={2} align="center">
            <Box
              bg="blackAlpha.800"
              color="white"
              px={6}
              py={3}
              borderRadius="lg"
              fontSize="lg"
              fontWeight="bold"
            >
              {skipAmount > 0 ? `+${skipAmount}s` : `${skipAmount}s`}
            </Box>
            <Text
              color="white"
              fontSize="sm"
              bg="blackAlpha.700"
              px={3}
              py={1}
              borderRadius="md"
            >
              {formatTime(currentTime)}
            </Text>
          </VStack>
        </Box>
      )}

      {/* Center Play Controls */}
      <VideoControls
        isPlaying={isPlaying}
        isLocked={isLocked}
        onPlayPause={handlePlayPause}
        onSkip={handleSkip}
        showControls={showControls}
      />

      {/* Right Side Interaction Bar */}
      <VideoInteractions
        showControls={showControls}
        isLiked={isLiked}
        isDisliked={isDisliked}
        likes={likes}
        dislikes={dislikes}
        onLike={handleLike}
        onDislike={handleDislike}
        onShare={handleShare}
        onDonate={handleDonate}
      />

      {/* Bottom Controls */}
      {showControls && (
        <Box
          position="absolute"
          bottom={0}
          left={0}
          right={0}
          zIndex={10}
          bg="linear-gradient(to top, rgba(0,0,0,0.8) 0%, transparent 100%)"
          p={4}
        >
          <VStack gap={4} align="stretch">
            {/* YouTube-style Progress Bar */}
            <VideoProgressBar
              currentTime={currentTime}
              duration={duration}
              bufferedRanges={bufferedRanges}
              isDragging={isDragging}
              hoverTime={hoverTime}
              showPreview={showPreview}
              isLocked={isLocked}
              onProgressMouseMove={handleProgressMouseMove}
              onProgressMouseLeave={handleProgressMouseLeave}
              onProgressClick={handleProgressClick}
              formatTime={formatTime}
              getProgressPercentage={getProgressPercentage}
              getBufferedPercent={getBufferedPercent}
            />

            {/* Channel Info and Subscribe */}
            <HStack justify="space-between" align="center" width="100%" mt={4}>
              <HStack gap={3} align="center">
                {creatorAvatar ? (
                  <CachedImage
                    src={creatorAvatar}
                    alt={uploaderName}
                    width="40px"
                    height="40px"
                    borderRadius="full"
                    objectFit="cover"
                    fallbackType="creator"
                  />
                ) : (
                  <Box
                    w="40px"
                    h="40px"
                    borderRadius="full"
                    bg="purple.600"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    color="white"
                    fontSize="sm"
                    fontWeight="bold"
                  >
                    {uploaderName.charAt(0).toUpperCase()}
                  </Box>
                )}
                    <VStack align="start" gap={0}>
                      <Text color="white" fontSize="md" fontWeight="bold">
                        {uploaderName}
                      </Text>
                      <HStack gap={3}>
                        <Text color="whiteAlpha.700" fontSize="sm">
                          {subscriberCount > 999
                            ? `${(subscriberCount / 1000).toFixed(1)}k`
                            : subscriberCount.toLocaleString()} followers
                        </Text>
                        <Text color="whiteAlpha.500" fontSize="sm">•</Text>
                        <Text color="whiteAlpha.700" fontSize="sm">
                          {viewCount > 999
                            ? `${(viewCount / 1000).toFixed(1)}k`
                            : viewCount.toLocaleString()} views
                        </Text>
                      </HStack>
                    </VStack>
              </HStack>

              {userUid !== creatorId && (
                <Button
                  size="sm"
                  colorScheme="purple"
                  variant={isSubscribed ? "outline" : "solid"}
                  onClick={handleSubscribe}
                >
                  {isSubscribed ? "Following" : "Follow"}
                </Button>
              )}
            </HStack>

            {/* Bottom Control Row */}
            <HStack justify="space-around" align="center">
              <VStack gap={1} cursor="pointer" onClick={handleEpisodesToggle}>
                <IconWrapper
                  icon={FiList}
                  size={24}
                  color={showEpisodes ? "purple.400" : "white"}
                />
                <Text
                  color={showEpisodes ? "purple.400" : "white"}
                  fontSize="sm"
                  fontWeight="medium"
                >
                  Episodes
                </Text>
              </VStack>

              <VStack gap={1} cursor="pointer" onClick={handleSubtitlesToggle}>
                <IconWrapper
                  icon={FiMessageCircle}
                  size={24}
                  color={showSubtitles ? "purple.400" : "white"}
                />
                <Text
                  color={showSubtitles ? "purple.400" : "white"}
                  fontSize="sm"
                  fontWeight="medium"
                >
                  {showSubtitles ? "Subtitle on" : "Subtitle off"}
                </Text>
              </VStack>

              <VStack gap={1} cursor="pointer" onClick={handleSpeedChange}>
                <IconWrapper icon={FiSettings} size={24} color="white" />
                <Text color="white" fontSize="sm" fontWeight="medium">
                  Speed ({playbackSpeed}x)
                </Text>
              </VStack>

              <VStack gap={1} cursor="pointer" onClick={handleLockToggle}>
                <IconWrapper
                  icon={FiLock}
                  size={24}
                  color={isLocked ? "purple.400" : "white"}
                />
                <Text
                  color={isLocked ? "purple.400" : "white"}
                  fontSize="sm"
                  fontWeight="medium"
                >
                  {isLocked ? "Unlock" : "Lock"}
                </Text>
              </VStack>
            </HStack>
          </VStack>
        </Box>
      )}
    </>
  );
};

export default VideoPlayerLayout;
